/**
 * Policies (Salesforce Opportunities) in the list views the org used (spec 7): Book of Business is
 * the agent's own ledger, best-paying first; the rest cover whatever the person may see, cut to
 * their team or branch by the scope picker. View, scope and search live in the URL.
 */
import { type PolicySummary, type RecordScope } from "@tlcrm/shared";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { usePolicies } from "../api/hooks";
import { useMe } from "../auth/session";
import { DataTable, type Column } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { CommissionFlag, PostSaleMark } from "../components/ui";
import { money, SCOPE_LABEL } from "../lib/format";
import { NewPolicyModal } from "../forms/NewPolicy";
import { dealColumns } from "./dashboards/dealColumns";

const VIEWS = [
  ["all", "All"],
  ["book", "Book of Business"],
  ["won", "Won"],
  ["pending", "Pending"],
  ["this_week", "This week"],
  ["unpaid", "Unpaid"],
  ["cancelled", "Cancelled"],
  ["rewrite", "Rewrites"],
  ["effective", "Currently effective"],
] as const;
type View = (typeof VIEWS)[number][0];

const yesNo = (v: boolean) => (v ? "Yes" : <span className="t3">No</span>);

const c = {
  ...dealColumns,
  expected: {
    header: "Expected Commission",
    cell: (p: PolicySummary) => (
      <span className="cell-flags">
        <CommissionFlag status={p.commissionStatus} short />
        <span className="mono">{money(p.expectedCommission, true)}</span>
      </span>
    ),
    num: true,
  },
  paid: { header: "Commission Paid", cell: (p: PolicySummary) => yesNo(p.commissionPaid) },
  deducted: {
    header: "Deducted",
    cell: (p: PolicySummary) =>
      p.deducted ? (
        <span className="mono">{money(p.deductedAmount, true)}</span>
      ) : (
        <span className="t3">No</span>
      ),
    num: true,
  },
  product: {
    header: "Policy Type",
    cell: (p: PolicySummary) => p.product ?? <span className="t3">–</span>,
  },
  postSaleOnly: {
    header: "Post-Sale Status",
    cell: (p: PolicySummary) => <PostSaleMark status={p.postSaleStatus} />,
  },
} satisfies Record<string, Column<PolicySummary>>;

const COLUMNS: Record<View, Column<PolicySummary>[]> = {
  all: [
    c.policy,
    c.stage,
    c.dateSold,
    c.carrier,
    c.product,
    c.premium,
    c.expected,
    c.postSaleOnly,
    c.owner,
  ],
  book: [
    c.policy,
    c.stage,
    c.dateSold,
    c.carrier,
    c.premium,
    c.paid,
    c.expected,
    c.postSaleOnly,
    c.deducted,
  ],
  won: [c.policy, c.dateSold, c.carrier, c.product, c.premium, c.expected, c.paid, c.owner],
  pending: [c.customer, c.stage, c.premium, c.callType, c.dateSold, c.effective, c.owner],
  this_week: [c.policy, c.stage, c.dateSold, c.carrier, c.premium, c.expected, c.owner],
  unpaid: [c.policy, c.stage, c.premium, c.carrier, c.dateSold, c.effective, c.expected, c.owner],
  cancelled: [c.policy, c.customer, c.stage, c.dateSold, c.premium, c.cancelled, c.reason, c.owner],
  rewrite: [c.policy, c.dateSold, c.premium, c.carrier, c.owner],
  effective: [c.policy, c.carrier, c.product, c.premium, c.effective, c.owner],
};

export function Policies() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const view = (VIEWS.find(([v]) => v === params.get("view"))?.[0] ?? "all") as View;
  const scope = (params.get("scope") as RecordScope | null) ?? undefined;
  const [q, setQ] = useState(params.get("q") ?? "");
  const policies = usePolicies({
    view,
    scope: view === "book" ? undefined : scope,
    q: q.trim() || undefined,
  });
  const rows = policies.data?.pages.flatMap((p) => p.items);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Policies</h1>
          <p>Every sale, from the day it is sold. Sales reports count Closed Won.</p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" /> New policy
          </button>
        </div>
      </div>
      <div className="toolbar">
        <div className="seg" role="tablist" aria-label="List view">
          {VIEWS.map(([v, l]) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              onClick={() => setParam("view", v === "all" ? "" : v)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="toolbar">
        <div className="search">
          <Icon name="search" />
          <input
            className="field"
            placeholder="Customer or policy number"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setParam("q", e.target.value.trim());
            }}
            aria-label="Customer or policy number"
          />
        </div>
        {me.scopes.length > 1 && view !== "book" && (
          <select
            className="field"
            value={scope ?? ""}
            onChange={(e) => setParam("scope", e.target.value)}
            aria-label="Whose policies"
          >
            <option value="">Everything I can see</option>
            {me.scopes.map((s) => (
              <option key={s} value={s}>
                {SCOPE_LABEL[s]}
              </option>
            ))}
          </select>
        )}
        <span className="meta">
          {rows ? `${rows.length}${policies.hasNextPage ? "+" : ""} policies` : ""}
        </span>
      </div>
      <DataTable
        columns={COLUMNS[view]}
        rows={rows}
        href={(p) => `/policies/${p.id}`}
        loading={policies.isPending}
        error={policies.error}
        onRetry={() => void policies.refetch()}
        empty={{ icon: "briefcase", title: "No policies in this view" }}
        hasMore={policies.hasNextPage}
        loadingMore={policies.isFetchingNextPage}
        onMore={() => void policies.fetchNextPage()}
      />
      {creating && <NewPolicyModal onClose={() => setCreating(false)} />}
    </div>
  );
}
